import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { fieldEmojis, frenchAreas } from 'variables/industries';
import { useSavedRegions } from 'services/savedRegionsService';
import { useIndustries } from 'services/industryService';
import { useWalletBalance } from 'services/walletService';
import { CONFIG } from 'variables/config';
import { MdInfoOutline } from 'react-icons/md';

const PurchaseOrder = () => {
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const navigate = useNavigate();

    const buildIndustryDataFields = () => {
        const mapping: { [key: string]: any[] } = {};
        industriesData.forEach(industry => {
            const fields: any[] = [];
            if (industry.mandatoryFields && industry.mandatoryFields.length > 0) {
                industry.mandatoryFields.forEach((field: string) => {
                    fields.push({ label: field, icon: fieldEmojis[field] || '📋' });
                });
            }
            const key = industry.name.toLowerCase().replace(/\s+/g, '-');
            mapping[key] = fields;
        });
        return mapping;
    };

    const buildIndustryProducts = () => {
        const mapping: { [key: string]: string[] } = {};
        industriesData.forEach(industry => {
            const products = industry.subIndustries;
            if (products && Array.isArray(products)) {
                const productNames = products.map((p: any) => p.name);
                const key = industry.name.toLowerCase().replace(/\s+/g, '-');
                mapping[key] = productNames;
            }
        });
        return mapping;
    };

    const industryDataFields = buildIndustryDataFields();
    const industryProducts = buildIndustryProducts();

    const [price, setPrice] = useState('15.00');
    const [leadCount, setLeadCount] = useState('50');
    const [industry, setIndustry] = useState('');
    const [product, setProduct] = useState('');
    const [departments, setDepartments] = useState<string[]>([]);
    const [showProductField, setShowProductField] = useState(true);
    const [dataFields, setDataFields] = useState<any[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [payingWithWallet, setPayingWithWallet] = useState(false);
    const { savedRegions, saveRegions } = useSavedRegions();
    const { walletBalance, loading: walletLoading } = useWalletBalance();

    useEffect(() => {
        if (savedRegions.length > 0 && departments.length === 0) {
            setDepartments(savedRegions);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedRegions]);

    useEffect(() => {
        if (industriesData.length > 0 && !industry) {
            const first = industriesData[0];
            const key = first.name.toLowerCase().replace(/\s+/g, '-');
            setIndustry(key);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industriesData]);

    useEffect(() => {
        if (industry && industryDataFields[industry]) {
            setDataFields(industryDataFields[industry]);
        }
        if (industry && industryProducts[industry]) {
            setShowProductField(true);
            const firstProduct = industryProducts[industry][0].toLowerCase().replace(/\s+/g, '-');
            setProduct(firstProduct);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industry, industriesData]);

    const validateForm = () => {
        const leadCountNum = parseInt(leadCount);
        const priceNum = parseFloat(price);

        if (leadCountNum < 1) { alert('Veuillez indiquer un nombre de leads souhaite'); return false; }
        if (priceNum <= 0) { alert('Le prix doit etre superieur a 0€'); return false; }
        if (!industry) { alert('Veuillez selectionner une industrie'); return false; }
        if (!product) { alert('Veuillez selectionner un produit'); return false; }
        if (!departments || departments.length === 0) { alert('Veuillez selectionner au moins un departement'); return false; }
        return true;
    };

    const createPurchaseOrder = async () => {
        const token = localStorage.getItem('token');
        const leadCountNum = parseInt(leadCount);
        const priceNum = parseFloat(price);

        const userResponse = await axios.get(`${CONFIG.apiUrl}/user/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const userId = userResponse.data._id;

        const response = await axios.post(
            `${CONFIG.apiUrl}/purchase-order`,
            { price: priceNum, leadCount: leadCountNum, industry, product, departments, dataFields },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        return { purchaseOrderId: response.data.purchaseOrder._id, userId, token };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const { purchaseOrderId, token } = await createPurchaseOrder();
            const checkoutResponse = await axios.post(
                `${CONFIG.apiUrl}/stripe/checkout-purchase-order/${purchaseOrderId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            window.location.href = checkoutResponse.data.url;
        } catch (error: any) {
            console.error('Error creating purchase order:', error);
            alert(`Erreur lors de la creation de l'ordre d'achat:\n${error.response?.data?.message || error.message}`);
            setSubmitting(false);
        }
    };

    const handlePayWithWallet = async () => {
        if (!validateForm()) return;

        setPayingWithWallet(true);
        try {
            const { purchaseOrderId, token } = await createPurchaseOrder();
            await axios.post(
                `${CONFIG.apiUrl}/purchase-order/${purchaseOrderId}/pay-with-wallet`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            navigate('/admin/paiement-success?type=purchase-order');
        } catch (error: any) {
            console.error('Error paying purchase order with wallet:', error);
            alert(`Erreur lors du paiement:\n${error.response?.data?.message || error.message}`);
            setPayingWithWallet(false);
        }
    };

    const totalAmount = (parseFloat(price || '0') * parseInt(leadCount || '0'));
    const hasEnoughBalance = walletBalance >= totalAmount && totalAmount > 0;

    if (industriesLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-leadrs-stroke border-t-leadrs-brand" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                {/* Title card */}
                <div className="flex-1 rounded-xl border border-leadrs-stroke bg-white p-6">
                    <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                        Achat de leads
                    </p>
                    <h1 className="mt-1 font-crimson text-4xl font-semibold leading-tight text-leadrs-text">
                        Ordre d&apos;achat
                    </h1>
                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                        La bourse du lead. Vous fixez vos conditions, plus vous payez plus vous etes prioritaire. Ideal pour des leads locaux sans engagement.
                    </p>
                </div>

                {/* Comment ca marche */}
                <div className="rounded-xl bg-leadrs-blue-soft p-6 lg:w-[340px] lg:self-start">
                    <div className="mb-3 flex items-center gap-2">
                        <MdInfoOutline className="h-5 w-5 text-leadrs-blue-focus" />
                        <p className="font-geist text-sm font-semibold text-leadrs-blue-focus">Comment ca marche ?</p>
                    </div>
                    <ul className="flex flex-col gap-2">
                        {[
                            { label: 'Fixez votre prix', desc: 'Definissez le prix exact que vous souhaitez payer par lead' },
                            { label: 'Matching automatique', desc: 'La plateforme fait le matching entre les leads des vendeurs et vos conditions' },
                            { label: 'Systeme de priorite', desc: 'Plus votre prix est eleve, plus vous etes prioritaire sur votre zone geographique' },
                        ].map(({ label, desc }) => (
                            <li key={label} className="flex items-start gap-2">
                                <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-leadrs-blue-focus text-center font-geist text-[10px] font-bold leading-4 text-white">✓</span>
                                <p className="font-geist text-xs text-leadrs-blue-focus opacity-90">
                                    <strong>{label} :</strong> {desc}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Form card */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        {/* Prix maximum */}
                        <div>
                            <label className="mb-1.5 block font-geist text-sm font-medium text-leadrs-text">
                                Prix maximum par lead <span className="text-leadrs-red-focus">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    className="w-full rounded-lg border border-leadrs-stroke bg-white px-4 py-2.5 pr-10 font-geist text-sm text-leadrs-text outline-none transition-colors focus:border-leadrs-brand"
                                    placeholder="15.00"
                                    required
                                />
                                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 font-geist text-sm font-semibold text-leadrs-text-soft">
                                    €
                                </span>
                            </div>
                            <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                Le prix que vous etes pret a payer pour chaque lead
                            </p>
                        </div>

                        {/* Nombre de leads */}
                        <div>
                            <label className="mb-1.5 block font-geist text-sm font-medium text-leadrs-text">
                                Nombre de leads souhaites <span className="text-leadrs-red-focus">*</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={leadCount}
                                onChange={(e) => setLeadCount(e.target.value)}
                                className="w-full rounded-lg border border-leadrs-stroke bg-white px-4 py-2.5 font-geist text-sm text-leadrs-text outline-none transition-colors focus:border-leadrs-brand"
                                placeholder="50"
                                required
                            />
                            <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                Nombre estime de leads souhaites (pas de minimum de volume)
                            </p>
                        </div>

                        {/* Industrie */}
                        <div>
                            <label className="mb-1.5 block font-geist text-sm font-medium text-leadrs-text">
                                Industrie <span className="text-leadrs-red-focus">*</span>
                            </label>
                            <select
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                className="w-full rounded-lg border border-leadrs-stroke bg-white px-4 py-2.5 font-geist text-sm text-leadrs-text outline-none transition-colors focus:border-leadrs-brand"
                                required
                            >
                                <option value="">Selectionner une industrie</option>
                                {industriesData.map((industryItem) => {
                                    const key = industryItem.name.toLowerCase().replace(/\s+/g, '-');
                                    return (
                                        <option key={industryItem._id} value={key}>
                                            {industryItem.name}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {/* Produit */}
                        {showProductField && (
                            <div>
                                <label className="mb-1.5 block font-geist text-sm font-medium text-leadrs-text">
                                    Produit <span className="text-leadrs-red-focus">*</span>
                                </label>
                                <select
                                    value={product}
                                    onChange={(e) => setProduct(e.target.value)}
                                    className="w-full rounded-lg border border-leadrs-stroke bg-white px-4 py-2.5 font-geist text-sm text-leadrs-text outline-none transition-colors focus:border-leadrs-brand"
                                    required
                                >
                                    <option value="">Selectionner un produit</option>
                                    {industry && industryProducts[industry] &&
                                        industryProducts[industry].map((prod) => (
                                            <option
                                                key={prod}
                                                value={prod.toLowerCase().replace(/\s+/g, '-')}
                                            >
                                                {prod}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Data fields */}
                    {dataFields.length > 0 && (
                        <div className="mt-6 rounded-xl border border-leadrs-stroke bg-leadrs-blue-soft p-4">
                            <p className="mb-1 font-geist text-sm font-semibold text-leadrs-blue-focus">
                                Donnees incluses dans chaque lead
                            </p>
                            <p className="mb-3 font-geist text-xs text-leadrs-blue-focus opacity-70">
                                Ces champs sont fixes pour cette industrie et ne peuvent pas etre modifies.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {dataFields.map((field, index) => (
                                    <span
                                        key={index}
                                        className="rounded border border-leadrs-stroke bg-white px-2.5 py-1 font-geist text-xs text-leadrs-text"
                                    >
                                        {field.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Departements */}
                    <div className="mt-5">
                        <label className="mb-1.5 block font-geist text-sm font-medium text-leadrs-text">
                            Departements cibles <span className="text-leadrs-red-focus">*</span>
                        </label>
                        <Select
                            value={departments.map((region: string) => ({
                                value: region === 'Toute la France' ? 'france' : region,
                                label: region,
                            }))}
                            isMulti
                            onChange={(items) => {
                                const regions: string[] = [];
                                items.map((item) =>
                                    regions.push(item.value === 'france' ? 'Toute la France' : item.value)
                                );
                                setDepartments(regions);
                                saveRegions(regions);
                            }}
                            name="departments"
                            options={frenchAreas.map((item) => ({
                                value: item === '🇫🇷 Toute la France' ? 'france' : item,
                                label: item,
                            }))}
                            className="w-full font-geist text-sm"
                            classNamePrefix="select"
                            placeholder="Selectionner des departements..."
                        />
                        <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                            Ciblage local precis par departement, pas de minimum de departement.
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex gap-3">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="flex-1 rounded-lg border border-leadrs-stroke px-6 py-2.5 font-geist text-sm font-medium text-leadrs-text transition-colors hover:border-leadrs-brand"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || payingWithWallet}
                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-leadrs-brand px-6 py-2.5 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {submitting
                                ? 'Creation en cours...'
                                : `Payer par carte ${totalAmount.toFixed(2)}€`}
                        </button>
                    </div>
                    {!walletLoading && (
                        <div className="mt-3">
                            {hasEnoughBalance ? (
                                <button
                                    type="button"
                                    disabled={submitting || payingWithWallet}
                                    onClick={handlePayWithWallet}
                                    className="w-full rounded-lg border border-leadrs-stroke bg-white px-6 py-2.5 font-geist text-sm font-medium text-leadrs-text transition-colors hover:border-leadrs-brand disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {payingWithWallet
                                        ? 'Paiement en cours...'
                                        : `Payer avec mon solde (${walletBalance.toFixed(2)}€ disponible)`}
                                </button>
                            ) : (
                                <p className="text-center font-geist text-sm text-leadrs-text-soft">
                                    Solde de la cagnotte insuffisant ({walletBalance.toFixed(2)}€ disponible).{' '}
                                    <span
                                        className="cursor-pointer text-leadrs-brand underline hover:opacity-80"
                                        onClick={() => navigate('/admin/wallet/topup')}
                                    >
                                        Ajouter des crédits
                                    </span>
                                </p>
                            )}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default PurchaseOrder;
